import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeTokenManager — Generate/regenerate/delete security token with view dialog.
 * Wraps the token lifecycle for on-canvas usage.
 * Used by: Webhook, Trigger a Zap, Trigger from Zapier.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const ButtonRow = styled('div')({
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
});

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: NODE_UI.fontSize.value,
  fontWeight: NODE_UI.fontWeight.label,
  padding: '4px 12px',
  minHeight: 32,
  borderRadius: NODE_UI.borderRadiusSm,
}));

const StatusText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

const NodeTokenManager = React.memo(function NodeTokenManager({
  label = 'Security Token',
  hasToken = false,
  onGenerate,
  onRegenerate,
  onDelete,
  loading = false,
  sx,
}) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleGenerate = useCallback((e) => {
    stopPropagation(e);
    onGenerate?.();
  }, [onGenerate]);

  const handleRegenerate = useCallback((e) => {
    stopPropagation(e);
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmRegenerate = useCallback(() => {
    setConfirmDialogOpen(false);
    onRegenerate?.();
  }, [onRegenerate]);

  const handleDelete = useCallback((e) => {
    stopPropagation(e);
    onDelete?.();
  }, [onDelete]);

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      {label && <Label>{label}</Label>}

      {hasToken ? (
        <>
          <StatusText>
            <KeyIcon sx={{ fontSize: 14 }} /> Token is active
          </StatusText>
          <ButtonRow>
            <ActionButton
              variant="outlined"
              size="small"
              onClick={handleRegenerate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={12} /> : <AutorenewIcon sx={{ fontSize: 14 }} />}
            >
              Regenerate
            </ActionButton>
            <ActionButton
              variant="outlined"
              size="small"
              color="error"
              onClick={handleDelete}
              disabled={loading}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
            >
              Delete
            </ActionButton>
          </ButtonRow>
        </>
      ) : (
        <ButtonRow>
          <ActionButton
            variant="contained"
            size="small"
            onClick={handleGenerate}
            disabled={loading}
            disableElevation
            startIcon={loading ? <CircularProgress size={12} /> : <KeyIcon sx={{ fontSize: 14 }} />}
          >
            Generate Token
          </ActionButton>
        </ButtonRow>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px' } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Regenerate Token?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: NODE_UI.borderRadiusSm }}>
            The current token will be permanently invalidated. External systems using it will stop working.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRegenerate}
            variant="contained"
            color="warning"
            sx={{ textTransform: 'none' }}
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
});

export default NodeTokenManager;
